import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { toast } from "sonner";
import axios from "axios";
import { 
  RefreshCw, 
  Wifi, 
  WifiOff, 
  CheckCircle2, 
  XCircle, 
  AlertTriangle,
  Clock,
  ArrowRightLeft,
  Hotel
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RedfortsIntegration = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [autoChecking, setAutoChecking] = useState(false);

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API}/redforts/status`, { timeout: 10000 });
      setStatus(response.data);
    } catch (error) {
      console.error("Error fetching Redforts status:", error);
    } finally {
      setLoading(false);
    }
  };

  const autoCheckConnection = async () => {
    setAutoChecking(true);
    try {
      const response = await axios.post(`${API}/redforts/test-connection`, {}, { timeout: 60000 });
      if (response.data.connected) {
        setStatus(prev => ({ ...prev, connected: true, last_sync: new Date().toISOString() }));
      }
      fetchStatus();
    } catch (error) {
      console.error("Auto-check connection failed:", error);
    } finally {
      setAutoChecking(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const response = await axios.post(`${API}/redforts/test-connection`, {}, { timeout: 60000 });
      if (response.data.connected) {
        toast.success("Conexión exitosa con Redforts Oscar");
      } else {
        toast.error(response.data.message || "No se pudo conectar");
      }
      fetchStatus();
    } catch (error) {
      toast.error("Error al probar la conexión");
    } finally {
      setTesting(false);
    }
  };

  const syncOccupancy = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/redforts/sync`, {}, { timeout: 90000 });
      if (response.data.success) {
        toast.success(`Sincronización completada: ${response.data.message}`);
        if (response.data.prices_recalculated) {
          toast.info("Los precios han sido recalculados automáticamente");
        }
      } else {
        toast.error(response.data.message || "Error en la sincronización");
      }
      fetchStatus();
    } catch (error) {
      toast.error("Error al sincronizar con Redforts");
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    // Auto-check connection on load
    autoCheckConnection();
    
    // Re-check every 5 minutes
    const interval = setInterval(() => {
      fetchStatus();
    }, 300000);
    
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  const isConfigured = status?.configured;
  const isConnected = status?.connected;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="redforts-integration">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Integración Redforts Oscar</h1>
        <p className="text-muted-foreground">Sincroniza la ocupación automáticamente desde tu PMS</p>
      </div>

      {/* Status Card */}
      <Card data-testid="redforts-status-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                autoChecking ? "bg-blue-100 dark:bg-blue-900/30 animate-pulse" :
                isConnected ? "bg-emerald-100 dark:bg-emerald-900/30" : 
                isConfigured ? "bg-amber-100 dark:bg-amber-900/30" : 
                "bg-red-100 dark:bg-red-900/30"
              }`}>
                {autoChecking ? (
                  <RefreshCw className="w-6 h-6 text-blue-600 dark:text-blue-400 animate-spin" />
                ) : isConnected ? (
                  <Wifi className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                ) : (
                  <WifiOff className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                )}
              </div>
              <div>
                <CardTitle>Estado de Conexión</CardTitle>
                <CardDescription>Redforts Oscar PMS</CardDescription>
              </div>
            </div>
            <Badge variant={isConnected ? "default" : "secondary"} className={
              autoChecking ? "bg-blue-500 animate-pulse" :
              isConnected ? "bg-emerald-500" : ""
            }>
              {autoChecking ? "Verificando..." : isConnected ? "Conectado" : isConfigured ? "Desconectado" : "No configurado"}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">API Configurada</p>
              <div className="flex items-center gap-2 mt-1">
                {isConfigured ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-500" />
                )}
                <span className="font-medium">{isConfigured ? "Sí" : "No"}</span>
              </div>
            </div>
            <div className="p-4 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">Última Sincronización</p>
              <div className="flex items-center gap-2 mt-1">
                <Clock className="w-5 h-5 text-muted-foreground" />
                <span className="font-medium">
                  {status?.last_sync 
                    ? new Date(status.last_sync).toLocaleString("es-ES")
                    : "Nunca"
                  }
                </span>
              </div>
            </div>
            <div className="p-4 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground">URL de API</p>
              <p className="font-mono text-xs mt-1 truncate">
                {status?.api_url || "No configurada"}
              </p>
            </div>
          </div>

          {status?.error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Error de conexión</AlertTitle>
              <AlertDescription>{status.error}</AlertDescription>
            </Alert>
          )}

          {!isConnected && isConfigured && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Motor de reservas posiblemente desactivado</AlertTitle>
              <AlertDescription>
                Verifica en tu panel de Redforts Oscar que el motor de reservas online esté activado.
                Ve a <strong>Configuración → Motor de reservas</strong> y asegúrate de que esté habilitado.
              </AlertDescription>
            </Alert>
          )}

          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={testConnection} 
              disabled={testing || !isConfigured}
              data-testid="test-connection-btn"
            >
              {testing ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Wifi className="w-4 h-4 mr-2" />
              )}
              Probar Conexión
            </Button>
            <Button 
              onClick={syncOccupancy} 
              disabled={syncing || !isConfigured}
              data-testid="sync-occupancy-btn"
            >
              {syncing ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <ArrowRightLeft className="w-4 h-4 mr-2" />
              )}
              Sincronizar Ocupación
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* How it works */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">¿Cómo funciona?</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-bold">1</span>
              </div>
              <div>
                <h4 className="font-medium">Conexión con Oscar PMS</h4>
                <p className="text-sm text-muted-foreground">
                  El sistema se conecta a tu Redforts Oscar usando la API de reservas.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-bold">2</span>
              </div>
              <div>
                <h4 className="font-medium">Lectura de Ocupación</h4>
                <p className="text-sm text-muted-foreground">
                  Se obtiene la disponibilidad actual de cada tipo de habitación.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-bold">3</span>
              </div>
              <div>
                <h4 className="font-medium">Actualización Automática</h4>
                <p className="text-sm text-muted-foreground">
                  Los datos de ocupación se actualizan y los precios se recalculan según las reglas configuradas.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Setup Instructions */}
      {!isConfigured && (
        <Card className="border-amber-200 dark:border-amber-900">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Hotel className="w-5 h-5" />
              Configuración Requerida
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
              <li>Accede a tu panel de Redforts Oscar</li>
              <li>Ve a <strong>Configuración → Integración web → API de reservas</strong></li>
              <li>Copia el <strong>código de conexión</strong> y la <strong>URL base</strong></li>
              <li>Contacta al administrador para configurar las credenciales en el sistema</li>
            </ol>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default RedfortsIntegration;
